import React, { useState, useEffect, useMemo, useRef } from "react";
import { Card, CardContent, Box, CircularProgress } from "@mui/material";
import FilterComponents from "./filterComponents";
import OffersSummaryCard from "./SummaryCard";
import InnerTable from "lib/Tables/InnerTable";
import OfferColumns from "./models/offersColumns";
import MDBox from "components/MDBox";
import { get, post } from "requests";
import OffersFundingCalculator from "./OffersFundingCalculator";
import FullPageLoader from "components/Loader/FullPage";
import MDSnackbar from "components/MDSnackbar";
import { useStoreActions } from "easy-peasy";
import withDommer from "pages/withDommer";

const OffersPage: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [offers, setOffers] = useState([]);
  const [selectedOffer, setSelectedOffer] = useState<any>([]);
  const [editingOffer, setEditingOffer] = useState<any>({});
  const [showFundingCalculator, setShowFundingCalculator] = useState<any>(false);
  const [scheduleFrequency, setScheduleFrequency] = useState("weekly");
  const [loadingOffer, setLoadingOffer] = useState<boolean>(false);
  const [alert, setAlert] = useState(null);
  const [rules, setRules] = useState([]);

  const [formData, setFormData] = useState({
    fundingAmount: 0,
    factor: 0,
    purchasePrice: 0,
    bankFeePercentage: 0,
    bankFeeAmount: 0,
    totalCommissionPercentage: 0,
    totalCommissionAmount: 0,
    totalRefinance: 0,
    totalExternalPayoff: 0,
    totalFees: 0,
    leverage: 0,
    position: 1,
    specifiedPercentage: 0,
    fundingDate: new Date().toISOString().split("T")[0],
  });

  const [debits, setDebits] = useState<any>({
    daily: {
      recurringAmount: "",
      numTerms: "",
      totalBankDays: "",
      lastPaymentDate: "",
    },
    weekly: {
      recurringAmount: "",
      numTerms: "",
      totalBankDays: "",
      lastPaymentDate: "",
    },
    monthly: {
      recurringAmount: "",
      numTerms: "",
      totalBankDays: "",
      lastPaymentDate: "",
    },
  });

  const [filters, setFilters] = useState({
    fromDate: null,
    toDate: null,
    brokerIso: null,
    salesRep: null,
    internalRep: null,
    funder: null,
    searchTerm: "",
    status: null,
  });

  const [applicationFilters, setApplicationFilters] = useState({
    pending: true,
    funded: false,
    declined: false,
  });

  const [contractTerms, setContractTerms] = useState<any>({});
  const [MAX_FUNDER_HOLDBACK, SET_MAX_FUNDER_HOLDBACK] = useState<any>(0);

  const addOffer = useStoreActions((actions: any) => actions.offerStore.createOrUpdateOffer);
  const fetchRecalculatedApplication = useStoreActions(
    (actions: any) => actions.offerStore.fetchRecalculatedApplication
  );
  const handleBankAmountUpdate = useStoreActions(
    (actions: any) => actions.heldBackCommissionStore.handleForTotalBankFeeOrInvestorEditChanges
  );
  const updateSyndicatorsPercentage = useStoreActions(
    (actions: any) => actions.syndicationStore.updateSyndicatorsPercentage
  );

  const saveHeldback = useStoreActions(
    (actions: any) => actions.heldBackCommissionStore.saveHeldback
  );

  const deleteHeldBack = useStoreActions(
    (actions: any) => actions.heldBackCommissionStore.deleteCommission
  );

  const saveFinalOffer = useStoreActions((actions: any) => actions.offerStore.saveContractTerms);

  const onFilterChange = (key: string, value: any) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
  };

  const onApplicationFilterChange = (key: string, value: any) => {
    setApplicationFilters((prev) => ({ ...prev, [key]: value }));
  };

  const handleSummaryCardClick = (cardKey: string) => {
    const statusMap: Record<string, string> = {
      "All Offers": "",
      "Accepted Offers": "accepted",
      "Timed Out Offers": "timed out",
      "Offers Pending": "pending",
      "Pending Response": "pending response",
      "Response Sent" : "response sent"
    };

    setFilters((prev) => ({
      ...prev,
      status: statusMap[cardKey] || "",
    }));
  };

  const { filteredOffers, summaryData } = useMemo(
    () => calculateSummaryAndFilteredOffers(offers, filters, applicationFilters),
    [offers, filters, applicationFilters]
  );

  function matchesFilters(offer: any, filters: any, applicationFilters: any) {
    const { brokerIso, salesRep, internalRep, funder, fromDate, toDate, searchTerm } = filters;

    const offerSalesReps = offer.salesRep?.split(",").map((s: string) => s.trim()) || [];
    const offerInternalRep = offer.internalIsoRep?.trim() || "";
    const offerFunder = offer.funder?.trim() || "";
    const offerBrokerIso = offer.brokerIso?.trim() || "";
    const appStatus = offer.appStatus?.toLowerCase() || "";

    const createdAt = offer.dateSent ? new Date(offer.dateSent) : null;
    const isFunded = appStatus === "approved";
    const isDeclined = appStatus === "declined";
    const isPending = !isFunded && !isDeclined;

    const matchesAppStatus =
      (!applicationFilters?.funded && isFunded ? false : true) &&
      (!applicationFilters?.declined && isDeclined ? false : true) &&
      (!applicationFilters?.pending && isPending ? false : true);

    const matches =
      (!brokerIso || brokerIso === offerBrokerIso) &&
      (!salesRep || offerSalesReps.includes(salesRep)) &&
      (!internalRep || offerInternalRep === internalRep) &&
      (!funder || offerFunder === funder) &&
      (!fromDate || (createdAt && createdAt >= new Date(fromDate))) &&
      (!toDate || (createdAt && createdAt <= new Date(toDate))) &&
      (!searchTerm ||
        Object.values(offer).join(" ").toLowerCase().includes(searchTerm.toLowerCase())) &&
      matchesAppStatus;

    return matches;
  }

  function matchesOfferStatus(offer: any, selectedOfferStatus: string) {
    const offerStatus = offer.offerStatus?.toLowerCase() || "";
    const normalizedStatus = selectedOfferStatus.toLowerCase();
  
    const isPendingResponse = (offerStatus === "pending response")
    const isResponseSent =    (offerStatus === "response sent")

      
  
    const isPendingOffer = offerStatus === "sent" || offerStatus === "not sent";

    if (normalizedStatus === "accepted") {
      return offerStatus === "accepted";
    } else if (normalizedStatus === "timed out") {
      return offerStatus === "timed out";
    } else if (normalizedStatus === "pending") {
      return isPendingOffer;
    } else if (normalizedStatus === "pending response") {
      return isPendingResponse;
    }else if (normalizedStatus === "response sent") {
      return isResponseSent;
    }

    return false;
  }

  function calculateSummaryAndFilteredOffers(offers: any[], filters: any, applicationFilters: any) {
    const filtered: any[] = [];
    let totalOffers = 0;
    let pendingAmount = 0;
    let timedOutCount = 0;
    let acceptedCount = 0;
    let pendingResponseCount = 0;
    let responseSent =0;
  
    const selectedOfferStatus = filters.status || "";

    offers.forEach((offer) => {
      if (!matchesFilters(offer, filters, applicationFilters)) return;

      totalOffers += 1;

      const amount = parseFloat((offer.offerAmount || "0").toString().replace(/[^0-9.-]+/g, ""));

      const offerStatus = offer.offerStatus?.toLowerCase() || "";
  
      const isPendingResponse = (offerStatus === "pending response")
      const isResponseSent =    (offerStatus === "response sent")


  
      const isPendingOffer = offerStatus === "sent" || offerStatus === "not sent";

      if (isPendingOffer) pendingAmount += amount;
      if (isPendingResponse) pendingResponseCount += 1;
      if (isResponseSent) responseSent +=1;
      if (offerStatus === "accepted") acceptedCount += 1;
      if (offerStatus === "timed out") timedOutCount += 1;
    
  
      if (!selectedOfferStatus || matchesOfferStatus(offer, selectedOfferStatus)) {
        filtered.push(offer);
      }
    });

    return {
      filteredOffers: filtered,
      summaryData: [
        { title: "All Offers", value: `${totalOffers}`, color: "#666666" },
        {
          title: "Offers Pending",
          value: formatCurrency(pendingAmount),
          color: "#666666",
        },
        { title: "Pending Response", value: pendingResponseCount, color: "#666666" },
        { title: "Response Sent", value: responseSent, color: "#666666" },
        { title: "Accepted Offers", value: `${acceptedCount}`, color: "#4CAF50" },
        { title: "Timed Out Offers", value: `${timedOutCount}`, color: "#666666" },
      ],
    };
  }

  function formatCurrency(value: number): string {
    const absVal = Math.abs(value);
    if (absVal >= 1_000_000_000) {
      return `$${(value / 1_000_000_000).toFixed(2)}B`;
    } else if (absVal >= 1_000_000) {
      return `$${(value / 1_000_000).toFixed(2)}M`;
    } else if (absVal >= 1_000) {
      return `$${(value / 1_000).toFixed(2)}K`;
    } else {
      return `$${value.toFixed(2)}`;
    }
  }

  const getRowId = (row: any) => {
    const appId = row.dealApplicationId;
    const offerKey = row.offerKey;
    return `${appId}::${offerKey}`;
  };

  function evaluateConditions(row: any, conditions: any[]): boolean {
    return conditions.every((cond: any) => {
      const fieldValue = row[cond.field];
      switch (cond.operator) {
        case "==":
          return fieldValue == cond.value;
        case "!=":
          return fieldValue != cond.value;
        case "<":
          return fieldValue < cond.value;
        case "<=":
          return fieldValue <= cond.value;
        case ">":
          return fieldValue > cond.value;
        case ">=":
          return fieldValue >= cond.value;
        default:
          return false;
      }
    });
  }

  function getRowColor(row: any): string | null {
    for (const rule of rules) {
      if (evaluateConditions(row, rule.conditions)) {
        return rule.color;
      }
    }
    return `transparent`;
  }

  const selectRows = (id: any) => {
    if (selectedOffer?.length !== 0 && selectedOffer[0] === id) {
      setSelectedOffer([]);
    } else {
      setSelectedOffer([id]);
    }
  };

  useEffect(() => {
    fetchOffers();
  }, []);

  const fetchOffers = async () => {
    setLoading(true);
    try {
      const rulesStyles = await get("getOfferStylingRules");
      setRules(rulesStyles);
    } catch {
      console.log("Failed to fetch styling rules..");
    }
    const offers = await get("getAllOffers");
    setOffers(offers);
    setLoading(false);
  };

  const onEditOfferClick = async () => {
    if (!selectedOffer || !Array.isArray(selectedOffer) || selectedOffer.length === 0) {
      console.warn("No offer selected.");
      return;
    }

    const offerToEdit = offers.find((offr) => {
      const offerId = getRowId(offr);
      return offerId === selectedOffer[0];
    });

    if (offerToEdit) {
      try {
        setLoadingOffer(true);
        setEditingOffer(offerToEdit);
        const dealApplicationId = offerToEdit.dealApplicationId;
        const offerId = offerToEdit.offerKey;
        await fetchOfferData(dealApplicationId, offerId);
        setShowFundingCalculator(true);
      } finally {
        setLoadingOffer(false);
      }
    } else {
      console.warn("Selected offer not found.");
    }
  };

  const fetchOfferData = async (dealApplicationId: any, offerId: any) => {
    const contractTerms = await getContractTerms(dealApplicationId);
    const offer = await getOffer(dealApplicationId, offerId);
    await handleEditOffer(offer);
    const maxHoldBack = contractTerms?.contractTerms?.funder?.maxHoldback;
    SET_MAX_FUNDER_HOLDBACK(maxHoldBack);
    setContractTerms(contractTerms);
  };

  const handleEditOffer = async (row: any | null) => {
    const freq = row?.frequency || "daily";
    setScheduleFrequency(freq);

    const originalData: any = {
      fundingAmount: row?.fundingAmount || 1,
      factor: row?.factor || 1,
      purchasePrice: row?.paybackAmount || 1,
      bankFeePercentage: row?.bankFeePercentage || 0,
      bankFeeAmount: row?.bankFeeAmount || 0,
      totalCommissionPercentage: row?.commissionPercentage || 0,
      totalCommissionAmount: row?.commissionAmount || 0,
      totalRefinance: row.refinanceAmount || 0,
      totalExternalPayoff: row.externalPayOffAmount || 0,
      totalFees: row.otherFees || 0,
      leverage: row.leverage && !isNaN(row.leverage) ? row.leverage : 0,
      specifiedPercentage:
        row.specifiedPercentage && !isNaN(row.specifiedPercentage) ? row.specifiedPercentage : 0,
      fundingDate: row.fundingDate || new Date().toISOString().split("T")[0],
      position: row.position || 0,
    };

    if (row?.offerKey) {
      originalData.offerKey = row?.offerKey;
    }

    if (row?.funderId) {
      originalData.funderId = row?.funderId;
    }

    if (row?.funderName) {
      originalData.funderName = row?.funderName;
    }

    if (row?.type) {
      originalData.type = row?.type;
    }

    if (row?.status) {
      originalData.status = row?.status;
    }

    const originalSchedules = {
      daily: {
        recurringAmount: freq === "daily" ? sanitizeAndToFloat(row?.recurringAmount) : 0,
        numTerms: freq === "daily" ? toNumber(row?.term) : 0,
        totalBankDays: freq === "daily" ? toNumber(row?.numberOfPayments) : 0,
        lastPaymentDate: freq === "daily" ? row?.estimatedPayoffDate || "" : "",
      },
      weekly: {
        recurringAmount: freq === "weekly" ? sanitizeAndToFloat(row?.recurringAmount) : 0,
        numTerms: freq === "weekly" ? toNumber(row?.term) : 0,
        totalBankDays: freq === "weekly" ? toNumber(row?.numberOfPayments) : 0,
        lastPaymentDate: freq === "weekly" ? row?.estimatedPayoffDate || "" : "",
      },
      monthly: {
        recurringAmount: freq === "monthly" ? sanitizeAndToFloat(row?.recurringAmount) : 0,
        numTerms: freq === "monthly" ? toNumber(row?.term) : 0,
        totalBankDays: freq === "monthly" ? toNumber(row?.numberOfPayments) : 0,
        lastPaymentDate: freq === "monthly" ? row?.estimatedPayoffDate || "" : "",
      },
    };
    setDebits(originalSchedules);
    setFormData(originalData);
  };

  const sanitizeAndToFloat = (value: any) => {
    if (!value) {
      return 0;
    }

    const stringValue = String(value);
    const sanitizedValue = stringValue.replace(/[^0-9.-]/g, "");
    const floatValue = parseFloat(sanitizedValue);
    return isNaN(floatValue) ? 0 : floatValue;
  };

  const toNumber = (value: any) => {
    return isNaN(Number(value)) ? 0 : Number(value);
  };

  const getContractTerms = async (dealApplicationId: any) => {
    try {
      const response = await get(`getContractTerms/${dealApplicationId}`);
      return response;
    } catch (error) {
      return {};
    }
  };

  const onEditCellChange = async (rowData: any, columnId: string, newValue: string, sendEmail: boolean, emailContent: any, replyOnSameThread: boolean,recipients: any) => {
    try {
      setLoading(true);
      const appId = rowData.dealApplicationId;
      const offerKey = rowData.offerKey;


      if (appId && offerKey && newValue) {
        const reqBody: any = {
          dealApplicationId: appId,
          offerKey: offerKey,
        };

        if (columnId === "counterOffer") {
          reqBody.counterOffer = newValue;
          reqBody.sendEmail= sendEmail;
          reqBody.emailContent= emailContent;
          reqBody.recipients= recipients;
          reqBody.replyOnSameThread = replyOnSameThread;
          await post("editOfferDetailsFields", reqBody);
        } else if (columnId === "offerNotes") {
          reqBody.offerNotes = newValue;
          await post("editOfferDetailsFields", reqBody);
        } else if (columnId === "rebuttal") {
          reqBody.rebuttal = newValue;
          await post("editOfferDetailsFields", reqBody);
        } else if (columnId === "offerStatus") {
          await updateOfferStatus(appId, offerKey, newValue);
          await fetchOffers();
          return;
        }

        setOffers((prevOffers) =>
          prevOffers.map((offer) =>
            offer.dealApplicationId === appId && offer.offerKey === offerKey
              ? {
                  ...offer, 
                  [columnId]: newValue, 
                  offerStatus: columnId === "rebuttal" ? "Pending Response" :
                              columnId === "counterOffer" ? "Response Sent" :
                              offer.offerStatus,
                }
              : offer
          )
        );
      }
    } catch (error) {
      console.error("Failed to update offer field:", error);
      setAlert({
        type: "error",
        message: "Failed to update offer field.",
        timeOut: 4000,
      });
    } finally {
      setLoading(false);
    }
  };

  const updateOfferStatus = async (appId: any, offerKey: string, newStatus: string) => {
    const offer: any = await getOffer(appId, offerKey);
    const statusContractTerms = await getContractTerms(appId);
    offer.applicationId = appId;

    if (offer.status === newStatus) {
      return;
    }
    const oldStatus = offer.status;
    offer.status = newStatus;
    const response = await addOffer(offer);
    if (response?.acceptedOffer) {
      await handleAcceptedOffer(response.acceptedOffer, statusContractTerms, appId);
    } else if (oldStatus && oldStatus === "Accepted" && offer?.status !== "Accepted") {
      await clearAcceptedOffer(statusContractTerms, appId);
    }
    const offerDetails = await getContractTerms(appId);
    await handleBankAmountUpdate({
      offerDetails: offerDetails,
      dealApplicationId: appId,
    });
    await fetchRecalculatedApplication(appId);
    await updateSyndicatorsPercentage({
      applicationId: appId,
      reload: false,
    });
  };

  const handleAcceptedOffer = async (
    acceptedOffer: any,
    statusContractTerms: any,
    dealApplicationId: any
  ) => {
    const newContractTerms = statusContractTerms;
    newContractTerms.applicationId = dealApplicationId;
    newContractTerms.tenantId = 1;
    newContractTerms.bankFee = acceptedOffer?.bankFeeAmount;
    newContractTerms.bankFeePercentage = acceptedOffer?.bankFeePercentage;
    newContractTerms.commissionTotal = acceptedOffer?.commissionAmount;
    newContractTerms.bankFeeCalculationThrough = "bank_fee_percentage";
    newContractTerms.paybackAmountCalculationThrough = "payback_factor";

    newContractTerms.contractTerms.collectionFrequency = acceptedOffer?.frequency;
    newContractTerms.contractTerms.collectionRecurringAmount = acceptedOffer?.recurringAmount;
    newContractTerms.contractTerms.commissionAmount = acceptedOffer?.commissionAmount;
    newContractTerms.contractTerms.commissionPercentage = acceptedOffer?.commissionPercentage;
    newContractTerms.contractTerms.funder = { id: acceptedOffer?.funderId };
    newContractTerms.contractTerms.heldbackAmount = acceptedOffer?.otherFees;
    newContractTerms.contractTerms.numberOfDebits = acceptedOffer?.numberOfPayments;
    newContractTerms.numberOfDebits = acceptedOffer?.numberOfPayments;

    newContractTerms.contractTerms.fundedAmountChange = true;
    newContractTerms.contractTerms.commissionPercentageCalculationThrough = "commission_percentage";
    newContractTerms.contractTerms.fundingDate = acceptedOffer?.fundingDate;

    newContractTerms.fundedAmount = acceptedOffer?.fundingAmount;
    newContractTerms.purchaseAmount = acceptedOffer?.paybackAmount;
    newContractTerms.paybackFactor = acceptedOffer?.factor;
    newContractTerms.estimatedPayoffDate = acceptedOffer?.estimatedPayoffDate;
    newContractTerms.contractTerms.receivablePercentage = acceptedOffer?.specifiedPercentage;

    const refiDeals = newContractTerms.contractTerms.payingOffDeals;
    let newRefiDeals: any = [];
 
    const refinanceAmount = acceptedOffer?.refinanceAmount || 0;

    if (refinanceAmount > 0 && refiDeals && refiDeals.length > 0) {
      const refiDeal = refiDeals[0];
      refiDeal.payingoffAmount = refinanceAmount;
      newRefiDeals = [refiDeal];
    } else {
      newRefiDeals = [];
    }

    if (newRefiDeals && newRefiDeals.length > 0) {
      newContractTerms.contractTerms.parentDealAmount = refinanceAmount;
      newContractTerms.contractTerms.payingOffDeals = newRefiDeals;
    } else {
      newContractTerms.contractTerms.parentDealAmount = 0;
      newContractTerms.contractTerms.payingOffDeals = [];
    }

    //handle external payoff
    if (acceptedOffer.externalPayOffAmount && acceptedOffer.externalPayOffAmount > 0) {
      const tenantId = localStorage.getItem("tenantId");
      const externalFunders = await get(`fetchExternalFunders/${tenantId}`);
      const defaultExternalFunder = externalFunders[0];
      if (defaultExternalFunder) {
        newContractTerms.contractTerms.externalAbaNumber = defaultExternalFunder?.externalAbaNumber;
        newContractTerms.contractTerms.externalAccountNumber =
          defaultExternalFunder?.externalAccountNumber;
        newContractTerms.contractTerms.externalAmount = acceptedOffer?.externalPayOffAmount;
        newContractTerms.contractTerms.externalBankName = defaultExternalFunder?.externalBankName;
        newContractTerms.contractTerms.externalFunderId = defaultExternalFunder?.externalFunderId;
        newContractTerms.contractTerms.mcaCompany = defaultExternalFunder?.mcaCompany;
      }
    } else {
      newContractTerms.contractTerms.externalFunderId = null;
    }

    await saveFinalOffer(newContractTerms);

    //heldbacks
    const heldbacksResponse = await post("fetchApplicationHeldbacks", {
      applicationId: dealApplicationId,
    });
    const heldbacksData = heldbacksResponse["heldbacks"] || [];
    const otherFees = heldbacksData.filter((fee: any) => fee.type != "bank_fee");

    if (otherFees.length > 0) {
      if (otherFees.length > 0 && acceptedOffer?.otherFees && acceptedOffer?.otherFees > 0) {
        const fee = otherFees[0];
        fee.amount = acceptedOffer?.otherFees;
        fee.partyId = fee?.applicationPartyId;

        if (otherFees.length > 1) {
          for (let i = 1; i < otherFees.length; i++) {
            let remainingHeldBack = otherFees[i];
            await deleteHeldBack({
              id: remainingHeldBack.id,
              type: "",
              applicationId: dealApplicationId,
              deletingIds: [],
              callback: undefined,
            });
          }
        }

        await saveHeldback({
          offerDetails: newContractTerms,
          heldbackData: fee,
          dealApplicationId: dealApplicationId,
          callback: undefined,
        });
      } else {
        for (let i = 0; i < otherFees.length; i++) {
          let remainingHeldBack = otherFees[i];
          await deleteHeldBack({
            id: remainingHeldBack.id,
            type: "",
            applicationId: dealApplicationId,
            deletingIds: [],
            callback: undefined,
          });
        }
      }
    } else if (acceptedOffer?.otherFees && acceptedOffer?.otherFees > 0) {
      const fee = {
        amount: acceptedOffer?.otherFees,
        applicationId: dealApplicationId,
        description: "",
        partyId: acceptedOffer?.funderId || newContractTerms?.contractTerms?.funder?.funderId,
        tenantId: "1",
        type: "origination_fee",
      };
      await saveHeldback({
        offerDetails: newContractTerms,
        heldbackData: fee,
        dealApplicationId: dealApplicationId,
        callback: undefined,
      });
    }
  };

  const clearAcceptedOffer = async (statusContractTerm: any, dealApplicationId: any) => {
    const newContractTerms = statusContractTerm;
    newContractTerms.applicationId = dealApplicationId;
    newContractTerms.tenantId = 1;
    newContractTerms.bankFee = 0;
    newContractTerms.bankFeePercentage = 0;
    newContractTerms.commissionTotal = 0;

    newContractTerms.contractTerms.collectionFrequency = "";
    newContractTerms.contractTerms.collectionRecurringAmount = 0;
    newContractTerms.contractTerms.commissionAmount = 0;
    newContractTerms.contractTerms.commissionPercentage = 0;
    newContractTerms.contractTerms.heldbackAmount = 0;
    newContractTerms.contractTerms.numberOfDebits = 0;

    newContractTerms.numberOfDebits = 0;
    newContractTerms.contractTerms.receivablePercentage = 0;

    newContractTerms.fundedAmount = 0;
    newContractTerms.purchaseAmount = 0;
    newContractTerms.paybackFactor = 0;
    newContractTerms.estimatedPayoffDate = "";

    await saveFinalOffer(newContractTerms);
  };

  const getOffer = async (dealApplicationId: any, offerKey: any) => {
    try {
      const response = await get(
        `getOfferByDealApplicationIdAndOfferId/${dealApplicationId}/${offerKey}`
      );
      return response;
    } catch (error) {
      return {};
    }
  };

  return (
    <Box
      sx={{
        p: 2,
        height: "100vh",
        overflow: "hidden",
        marginLeft: "250px",
        transition: "all 0.3s ease",
        position: "relative",
      }}
    >
      {loading ? (
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            zIndex: 10,
          }}
        >
          <CircularProgress size={60} thickness={5} />
        </Box>
      ) : (
        <>
          <FilterComponents
            offers={offers}
            filters={filters}
            onFilterChange={onFilterChange}
            setFilters={setFilters}
            setApplicationFilters={setApplicationFilters}
            selectedOffer={selectedOffer}
            onEditOfferClick={onEditOfferClick}
          />

          <Card
            elevation={3}
            sx={{ width: "100%", mb: 1, borderRadius: 2, border: "1px solid #e0e0e0", ml: 1 }}
          >
            <CardContent>
              <OffersSummaryCard
                filters={filters}
                applicationFilters={applicationFilters}
                summaryData={summaryData}
                onCardClick={handleSummaryCardClick}
                onFilterChange={onApplicationFilterChange}
                filterOptions={[
                  { label: "Pending Applications", value: "pending" },
                  { label: "Funded Applications", value: "funded" },
                  { label: "Declined Applications", value: "declined" },
                ]}
              />
              <Box>
                <InnerTable
                  table={{
                    columns: OfferColumns(onEditCellChange),
                    rows: filteredOffers,
                  }}
                  entriesPerPage={{ defaultValue: 50, entries: [50, 100, 150, 200] }}
                  canSearch={false}
                  paginationEnabled={true}
                  hasHeaders={false}
                  hasFilterComponent={false}
                  selectedItem={selectedOffer}
                  onSelectItem={selectRows}
                  getRowId={getRowId}
                  getRowColor={getRowColor}
                  enableScroll={true}
                />
              </Box>
            </CardContent>
          </Card>

          {loadingOffer && <FullPageLoader />}

          {showFundingCalculator && (
            <OffersFundingCalculator
              formData={formData}
              setFormData={setFormData}
              debits={debits}
              setDebits={setDebits}
              frequency={scheduleFrequency}
              setFrequency={setScheduleFrequency}
              open={showFundingCalculator}
              setOpen={setShowFundingCalculator}
              dealApplicationId={editingOffer.dealApplicationId}
              scheduleFrequency={scheduleFrequency}
              setScheduleFrequency={setScheduleFrequency}
              offerId={editingOffer.offerKey}
              contractTerms={contractTerms}
              setContractTerms={setContractTerms}
              MAX_FUNDER_HOLDBACK={MAX_FUNDER_HOLDBACK}
              SET_MAX_FUNDER_HOLDBACK={SET_MAX_FUNDER_HOLDBACK}
              refreshOffers={fetchOffers}
            />
          )}

          {alert && (
            <MDSnackbar
              color={alert?.type === "success" ? "success" : "error"}
              icon="notifications"
              title={alert?.type === "success" ? "Success" : "Error"}
              content={alert?.message}
              dateTime=""
              autoHideDuration={alert?.timeOut || 3000}
              open={alert !== null}
              close={() => setAlert(null)}
            />
          )}
        </>
      )}
    </Box>
  );
};

export default withDommer(OffersPage);
